import { useState, useEffect } from 'react';
import { Popover } from '../../Popover/Popover';
import { Typography } from '../../Typography/Typography';
import TagEditMenu from '../TagsEditMenu';
import { TAG_COLORS } from '../constants';
import {
    EditorContainer,
    AppliedTagsContainer,
    AddTagButton,
    AddTagIconButton,
} from './TagEditor.styles';
import PargodaTag from '../PargodaTag';
import { useTheme } from '@mui/material/styles';
import {
    organizationActions,
    organizationSelectors,
} from '../../../../actions/organization/redux';
import { useAppDispatch, useAppSelector } from '../../../base-redux';
import { Directions, ITag } from '../../../../common';
import {
    jobApplicationActions,
    ISetTagAction,
} from '../../../../actions/jobApplication/redux';
import { jobsActions } from '../../../../actions/jobs/redux';
import { Icon } from '../../Icon';
import { useTranslation } from 'react-i18next';

export type TagModelName = 'job_application' | 'job';

interface Props {
    isFilter?: boolean;
    isInTableRow?: boolean;
    externalAppliedTags?: Array<ITag>;
    objectId: number;
    modelName: TagModelName;
    disableTagRemoval?: boolean;
    disabled?: boolean;
}

const PargodaTagsEditor = (props: Props) => {
    const {
        isFilter = false,
        isInTableRow = false,
        externalAppliedTags = [],
        objectId,
        modelName,
        disableTagRemoval = false,
        disabled = false,
    } = props;
    const [tagsAnchorEl, setTagsAnchorEl] = useState<null | HTMLElement>(null);
    const [appliedTags, setAppliedTags] = useState<Array<ITag>>([]);
    const [existingTags, setExistingTags] = useState<Array<ITag>>([]);
    const dispatch = useAppDispatch();
    const tags = useAppSelector(organizationSelectors.getTags);
    const theme = useTheme();
    const { t } = useTranslation();

    useEffect(() => {
        if (tags) {
            setExistingTags(tags);
        }
    }, [tags]);

    useEffect(() => {
        if (externalAppliedTags) {
            setAppliedTags(externalAppliedTags);
        }
    }, [externalAppliedTags]);

    useEffect(() => {
        return () => {
            setTagsAnchorEl(null);
        };
    }, []);

    const handleTagsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setTagsAnchorEl(event.currentTarget);
    };

    const handleTagsMenuClose = () => {
        setTagsAnchorEl(null);
    };

    const handleTagCreate = (tagName: string) => {
        const existingColors = new Set(existingTags.map((tag) => tag.color));
        let newColor = TAG_COLORS.find((color) => !existingColors.has(color));

        if (!newColor) {
            newColor =
                TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
        }
        dispatch(
            organizationActions.createTag({
                label: tagName,
                color: newColor,
            })
        );
    };

    const handleTagRemove = (tagId: number) => {
        dispatch(organizationActions.deleteTag({ id: tagId }));
    };

    const handleTagApply = (tag: ITag) => {
        const payload: ISetTagAction = {
            tag: Number(tag.tag_id),
            object_id: objectId,
            model_name: modelName,
            isInTableRow,
        };
        if (modelName === 'job') {
            dispatch(jobsActions.applyTagToJob(payload));
        } else {
            dispatch(jobApplicationActions.applyTagToJobApplication(payload));
        }
    };

    const handleTagUnapply = (tagId: number) => {
        if (modelName === 'job') {
            dispatch(
                jobsActions.deleteTagFromJob({
                    id: tagId,
                    jobId: objectId,
                    isInTableRow,
                })
            );
        } else {
            dispatch(
                jobApplicationActions.deleteTagFromJobApplication({
                    id: tagId,
                    isInTableRow,
                    jobApplicationId: objectId,
                })
            );
        }
    };

    const handleTagUpdate = (tag: ITag) => {
        dispatch(organizationActions.updateTag(tag));
    };

    const isTagsMenuOpen = Boolean(tagsAnchorEl);
    const popOverId = isTagsMenuOpen ? 'tags-popover' : undefined;

    return (
        <EditorContainer>
            {!isFilter && !disabled && (
                <>
                    {isInTableRow ? (
                        <AddTagIconButton onClick={handleTagsMenuClick}>
                            <Icon name="AddRoundedIcon" size={20} />
                        </AddTagIconButton>
                    ) : (
                        <AddTagButton onClick={handleTagsMenuClick}>
                            <Icon name="AddRoundedIcon" size={20} />
                            <Typography variant="body2">
                                {t('tagsEditor.addTag')}
                            </Typography>
                        </AddTagButton>
                    )}
                </>
            )}
            <AppliedTagsContainer>
                {appliedTags.map((tag) => (
                    <PargodaTag
                        key={tag.tag_id}
                        tag={tag}
                        onRemove={
                            !disableTagRemoval && !disabled && handleTagUnapply
                        }
                    />
                ))}
            </AppliedTagsContainer>
            <Popover
                id={popOverId}
                open={isTagsMenuOpen}
                anchorEl={tagsAnchorEl}
                onClose={handleTagsMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal:
                        theme.direction === Directions.ltr ? 'left' : 'right',
                }}
            >
                <TagEditMenu
                    appliedTags={appliedTags}
                    existingTags={existingTags}
                    onTagCreate={handleTagCreate}
                    onTagRemove={handleTagRemove}
                    onTagUpdate={handleTagUpdate}
                    onTagApply={handleTagApply}
                    onTagUnapply={handleTagUnapply}
                />
            </Popover>
        </EditorContainer>
    );
};

export default PargodaTagsEditor;
